<!-- PropertyAllocationModal.vue -->
<template>
  <div class="modal fade" id="propertyAllocationModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-account-plus me-2"></i>
            Allocate Property - {{ property.propertyId }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Property Summary -->
          <div class="card border mb-4">
            <div class="card-header bg-primary text-white">
              <h6 class="mb-0 text-white">
                <i class="mdi mdi-information me-2"></i>
                Property Summary
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-8">
                  <h6>{{ property.name }}</h6>
                  <p class="text-muted mb-2">{{ property.description }}</p>
                  <small class="text-muted">{{ property.specificLocation }}</small>
                </div>
                <div class="col-md-4 text-end">
                  <h5 class="text-primary">{{ formatPrice(property.monthlyRate) }}</h5>
                  <small class="text-muted">per month</small>
                </div>
              </div>
            </div>
          </div>

          <form @submit.prevent="submitForm">
            <!-- Allocation Type -->
            <div class="card border mb-4">
              <div class="card-header bg-info text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-account-group me-2"></i>
                  Allocation Type
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label class="form-label">Allocation Type <span class="text-danger">*</span></label>
                    <select v-model="form.allocationType" class="form-select" required>
                      <option value="">Select Allocation Type</option>
                      <option value="LEASE">Lease/Rent</option>
                      <option value="HIRE">Equipment/Machinery Hire</option>
                      <option value="STAFF_ACCOMMODATION">Staff Accommodation</option>
                      <option value="DEPARTMENTAL_USE">Departmental Use</option>
                      <option value="CONTRACT">Contract Allocation</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Occupant Information -->
            <div class="card border mb-4">
              <div class="card-header bg-success text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-account me-2"></i>
                  {{ getOccupantTypeLabel() }} Information
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ getOccupantTypeLabel() }} Name <span class="text-danger">*</span></label>
                    <input v-model="form.occupantName" type="text" class="form-control"
                           :placeholder="getOccupantNamePlaceholder()" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                    <input v-model="form.occupantPhone" type="tel" class="form-control"
                           placeholder="+254712345678" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Email Address</label>
                    <input v-model="form.occupantEmail" type="email" class="form-control"
                           placeholder="email@example.com">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">National ID/Registration Number <span class="text-danger">*</span></label>
                    <input v-model="form.occupantId" type="text" class="form-control"
                           placeholder="12345678 or CPR/2020/123456" required>
                  </div>
                </div>

                <!-- Type-specific fields -->
                <div v-if="form.allocationType === 'LEASE'" class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Business Type</label>
                    <input v-model="form.businessType" type="text" class="form-control"
                           placeholder="e.g., Vegetable Vendor, Electronics Shop">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Business License Number</label>
                    <input v-model="form.businessLicense" type="text" class="form-control">
                  </div>
                </div>

                <div v-if="form.allocationType === 'STAFF_ACCOMMODATION'" class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Employee Number</label>
                    <input v-model="form.employeeNumber" type="text" class="form-control">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Department</label>
                    <select v-model="form.department" class="form-select">
                      <option value="">Select Department</option>
                      <option value="Health">Health</option>
                      <option value="Education">Education</option>
                      <option value="Agriculture">Agriculture</option>
                      <option value="Roads & Transport">Roads & Transport</option>
                      <option value="Water">Water</option>
                      <option value="Finance">Finance</option>
                      <option value="Administration">Administration</option>
                    </select>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Designation</label>
                    <input v-model="form.designation" type="text" class="form-control"
                           placeholder="e.g., County Health Director">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Payroll Number</label>
                    <input v-model="form.payrollNumber" type="text" class="form-control">
                  </div>
                </div>

                <div v-if="form.allocationType === 'DEPARTMENTAL_USE' || form.allocationType === 'HIRE'" class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ form.allocationType === 'DEPARTMENTAL_USE' ? 'Department' : 'Company/Organization' }}</label>
                    <input v-model="form.organization" type="text" class="form-control"
                           :placeholder="form.allocationType === 'DEPARTMENTAL_USE' ? 'e.g., Roads Department' : 'e.g., ABC Construction Ltd'">
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">{{ form.allocationType === 'DEPARTMENTAL_USE' ? 'Head of Department' : 'Contact Person' }}</label>
                    <input v-model="form.contactPerson" type="text" class="form-control">
                  </div>
                  <div v-if="form.allocationType === 'HIRE'" class="col-md-6 mb-3">
                    <label class="form-label">Project Name</label>
                    <input v-model="form.projectName" type="text" class="form-control"
                           placeholder="e.g., Kitengela-Isinya Road Project">
                  </div>
                  <div v-if="property.type === 'VEHICLE'" class="col-md-6 mb-3">
                    <label class="form-label">Assigned Driver</label>
                    <input v-model="form.assignedDriver" type="text" class="form-control">
                  </div>
                </div>
              </div>
            </div>

            <!-- Allocation Terms -->
            <div class="card border mb-4">
              <div class="card-header bg-warning text-dark">
                <h6 class="mb-0">
                  <i class="mdi mdi-calendar me-2"></i>
                  Allocation Terms
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Start Date <span class="text-danger">*</span></label>
                    <input v-model="form.startDate" type="date" class="form-control" required>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">End Date</label>
                    <input v-model="form.endDate" type="date" class="form-control">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Lease Duration</label>
                    <select v-model="form.leaseDuration" class="form-select">
                      <option value="">Select Duration</option>
                      <option value="1 Month">1 Month</option>
                      <option value="3 Months">3 Months</option>
                      <option value="6 Months">6 Months</option>
                      <option value="1 Year">1 Year</option>
                      <option value="2 Years">2 Years</option>
                      <option value="3 Years">3 Years</option>
                      <option value="5 Years">5 Years</option>
                      <option value="Indefinite">Indefinite</option>
                    </select>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Monthly Payment (KES)</label>
                    <input v-model="form.monthlyPayment" type="number" class="form-control" min="0">
                    <small class="text-muted">Standard rate: {{ formatPrice(property.monthlyRate) }}</small>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Security Deposit (KES)</label>
                    <input v-model="form.securityDeposit" type="number" class="form-control" min="0">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Payment Frequency</label>
                    <select v-model="form.paymentFrequency" class="form-select">
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Bi-Annual">Bi-Annual</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Terms -->
            <div class="card border">
              <div class="card-header bg-secondary text-white">
                <h6 class="mb-0 text-white">
                  <i class="mdi mdi-file-document me-2"></i>
                  Additional Terms & Conditions
                </h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-12 mb-3">
                    <label class="form-label">Special Terms & Conditions</label>
                    <textarea v-model="form.specialTerms" class="form-control" rows="4"
                              placeholder="Enter any special terms, conditions, or restrictions for this allocation"></textarea>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Approved By</label>
                    <input v-model="form.approvedBy" type="text" class="form-control" readonly>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Approval Date</label>
                    <input v-model="form.approvalDate" type="date" class="form-control" readonly>
                  </div>
                </div>

                <!-- Document Upload -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Supporting Documents</label>
                    <input type="file" class="form-control" multiple accept=".pdf,.jpg,.jpeg,.png">
                    <small class="text-muted">ID copy, business license, etc.</small>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Agreement Documents</label>
                    <input type="file" class="form-control" accept=".pdf">
                    <small class="text-muted">Signed lease/hire agreement</small>
                  </div>
                </div>

                <!-- Checkboxes -->
                <div class="row mt-3">
                  <div class="col-12">
                    <div class="form-check mb-2">
                      <input v-model="form.termsAccepted" class="form-check-input" type="checkbox" id="termsAccepted">
                      <label class="form-check-label" for="termsAccepted">
                        The occupant has read and accepted the terms and conditions
                      </label>
                    </div>
                    <div class="form-check mb-2">
                      <input v-model="form.documentsVerified" class="form-check-input" type="checkbox" id="documentsVerified">
                      <label class="form-check-label" for="documentsVerified">
                        All required documents have been verified
                      </label>
                    </div>
                    <div class="form-check">
                      <input v-model="form.paymentReceived" class="form-check-input" type="checkbox" id="paymentReceived">
                      <label class="form-check-label" for="paymentReceived">
                        Initial payment/deposit has been received
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            Cancel
          </button>
          <button @click="submitForm" type="button" class="btn btn-success" :disabled="!isFormValid">
            <i class="mdi mdi-content-save me-1"></i>
            Allocate Property
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "PropertyAllocationModal",
  props: {
    property: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {
        propertyId: '',
        allocationType: '',
        occupantName: '',
        occupantPhone: '',
        occupantEmail: '',
        occupantId: '',
        businessType: '',
        businessLicense: '',
        employeeNumber: '',
        department: '',
        designation: '',
        payrollNumber: '',
        organization: '',
        contactPerson: '',
        projectName: '',
        assignedDriver: '',
        startDate: '',
        endDate: '',
        leaseDuration: '',
        monthlyPayment: 0,
        securityDeposit: 0,
        paymentFrequency: 'Monthly',
        specialTerms: '',
        approvedBy: '',
        approvalDate: '',
        termsAccepted: false,
        documentsVerified: false,
        paymentReceived: false
      },
      sessionNames: ''
    }
  },
  computed: {
    isFormValid() {
      return this.form.allocationType !== '' &&
          this.form.occupantName.trim() !== '' &&
          this.form.occupantPhone.trim() !== '' &&
          this.form.occupantId.trim() !== '' &&
          this.form.startDate !== '' &&
          this.form.termsAccepted &&
          this.form.documentsVerified;
    }
  },
  watch: {
    property: {
      handler(newValue) {
        if (newValue && Object.keys(newValue).length > 0) {
          this.form.propertyId = newValue.id;
          this.form.monthlyPayment = newValue.monthlyRate || 0;
          this.form.securityDeposit = (newValue.monthlyRate || 0) * 2;
        }
      },
      deep: true,
      immediate: true
    },

    'form.leaseDuration'() {
      this.calculateEndDate();
    },

    'form.startDate'() {
      this.calculateEndDate();
    }
  },
  mounted() {
    this.sessionNames = sessionStorage.getItem("sessionNames") || "System Admin";
    this.form.approvedBy = this.sessionNames;
    this.form.approvalDate = this.getCurrentDate();
    this.form.startDate = this.getCurrentDate();
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    getCurrentDate() {
      return moment().format('YYYY-MM-DD');
    },

    getOccupantTypeLabel() {
      switch (this.form.allocationType) {
        case 'LEASE':
          return 'Tenant';
        case 'HIRE':
          return 'Hirer';
        case 'STAFF_ACCOMMODATION':
          return 'Staff Member';
        case 'DEPARTMENTAL_USE':
          return 'Department';
        case 'CONTRACT':
          return 'Contractor';
        default:
          return 'Occupant';
      }
    },

    getOccupantNamePlaceholder() {
      switch (this.form.allocationType) {
        case 'LEASE':
          return 'Enter tenant full name';
        case 'HIRE':
          return 'Enter company/individual name';
        case 'STAFF_ACCOMMODATION':
          return 'Enter employee full name';
        case 'DEPARTMENTAL_USE':
          return 'Enter department name';
        case 'CONTRACT':
          return 'Enter contractor name';
        default:
          return 'Enter full name';
      }
    },

    calculateEndDate() {
      if (this.form.startDate && this.form.leaseDuration) {
        const startDate = moment(this.form.startDate);
        let endDate;

        switch (this.form.leaseDuration) {
          case '1 Month':
            endDate = startDate.add(1, 'month');
            break;
          case '3 Months':
            endDate = startDate.add(3, 'months');
            break;
          case '6 Months':
            endDate = startDate.add(6, 'months');
            break;
          case '1 Year':
            endDate = startDate.add(1, 'year');
            break;
          case '2 Years':
            endDate = startDate.add(2, 'years');
            break;
          case '3 Years':
            endDate = startDate.add(3, 'years');
            break;
          case '5 Years':
            endDate = startDate.add(5, 'years');
            break;
          default:
            return;
        }

        this.form.endDate = endDate.format('YYYY-MM-DD');
      }
    },

    submitForm() {
      if (this.isFormValid) {
        const allocationData = {
          ...this.form,
          additionalInfo: this.getAdditionalInfo()
        };

        this.$emit('save-allocation', allocationData);
      }
    },

    getAdditionalInfo() {
      const additionalInfo = {};

      if (this.form.allocationType === 'LEASE') {
        additionalInfo.businessType = this.form.businessType;
        additionalInfo.businessLicense = this.form.businessLicense;
      } else if (this.form.allocationType === 'STAFF_ACCOMMODATION') {
        additionalInfo.employeeNumber = this.form.employeeNumber;
        additionalInfo.department = this.form.department;
        additionalInfo.designation = this.form.designation;
        additionalInfo.payrollNumber = this.form.payrollNumber;
      } else if (this.form.allocationType === 'DEPARTMENTAL_USE' || this.form.allocationType === 'HIRE') {
        additionalInfo.organization = this.form.organization;
        additionalInfo.contactPerson = this.form.contactPerson;
        additionalInfo.projectName = this.form.projectName;
        additionalInfo.assignedDriver = this.form.assignedDriver;
      }

      return additionalInfo;
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
}

.card-header.bg-primary h6,
.card-header.bg-info h6,
.card-header.bg-success h6,
.card-header.bg-warning h6,
.card-header.bg-secondary h6 {
  color: white !important;
}

.border {
  border-color: #dee2e6 !important;
}

.form-check-label {
  font-size: 0.9rem;
}

.text-muted {
  font-size: 0.8rem;
}
</style>